import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import type { MouseEventHandler, ReactNode } from 'react';

import { ButtonIconAdd } from '@adflow/frontend/ui-buttons';
import { lineClampStyle } from '@adflow/frontend/ui-common';
import { theme, typography } from '@adflow/frontend/ui-theme';
import type { NavAction } from '@adflow/frontend/util-router';
import { LinkNavAction } from '@adflow/frontend/util-router';

const PAGE_HEADER_HEIGHT = 72;

export const pageHeaderAddButtonStyles = css({
	padding: '0.5rem',
	marginLeft: '-0.5rem',
});

export const pageHeaderVerticalCenterStyles = css({
	display: 'flex',
	alignItems: 'center',
	minHeight: PAGE_HEADER_HEIGHT,
});

type Align = 'start' | 'center';

const styles = {
	pageHeader: css({
		// TODO: Remove this when top level layout is changed
		minHeight: PAGE_HEADER_HEIGHT,
		flexShrink: 0,

		display: 'flex',
		justifyContent: 'space-between',
		paddingLeft: '1rem',
		paddingRight: '1rem',
		gap: '1rem',
	}),
	headerLeft: (align: Align) =>
		css({
			display: 'flex',
			alignItems: align,
			gap: '1rem',
			minWidth: 0,
		}),
	headerRight: (align: Align) =>
		css({
			display: 'flex',
			alignItems: align,
			gap: '1.5rem',
		}),

	addButton: pageHeaderAddButtonStyles,
};

export interface PageHeaderProps {
	title: ReactNode;
	titleNavAction?: NavAction;
	addNavAction?: NavAction;
	onAddClick?: MouseEventHandler<HTMLButtonElement>;
	addDisabled?: boolean;
	leftSlot?: ReactNode;
	/** @default 'center' */
	align?: Align;
	testid?: string;
	children?: ReactNode;
	leftStyles?: CSSInterpolation;
	rightStyles?: CSSInterpolation;
	containerStyles?: CSSInterpolation;
	customLayout?: boolean;
}

export function PageHeader({
	title,
	titleNavAction,
	addNavAction,
	onAddClick,
	addDisabled,
	leftSlot,
	align = 'center',
	testid,
	children,
	leftStyles,
	rightStyles,
	containerStyles,
	customLayout,
}: PageHeaderProps) {
	const verticalCenter =
		align === 'center'
			? (x: ReactNode) => x
			: (x: ReactNode) => <div css={pageHeaderVerticalCenterStyles}>{x}</div>;

	return (
		<header css={[styles.pageHeader, containerStyles]} data-testid={testid}>
			<div css={[styles.headerLeft(align), leftStyles]}>
				{verticalCenter(
					<h1
						css={[
							typography.title,
							lineClampStyle(),
							titleNavAction
								? {
										// Indicate when the title is a link.
										color: theme.colors.text.tableHeader,
										cursor: 'pointer',
									}
								: null,
						]}
					>
						{titleNavAction ? (
							<LinkNavAction to={titleNavAction}>{title}</LinkNavAction>
						) : (
							title
						)}
					</h1>
				)}

				{addNavAction
					? verticalCenter(
							<LinkNavAction to={addNavAction}>
								<ButtonIconAdd
									onClick={onAddClick}
									css={styles.addButton}
									data-disabled={addDisabled}
								/>
							</LinkNavAction>
						)
					: onAddClick
						? verticalCenter(
								<ButtonIconAdd
									onClick={onAddClick}
									css={styles.addButton}
									data-disabled={addDisabled}
								/>
							)
						: null}
				{leftSlot}
			</div>

			{customLayout ? (
				children
			) : (
				<div css={[styles.headerRight(align), rightStyles]}>{children}</div>
			)}
		</header>
	);
}
